import React from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Heart,
  Globe,
  ShieldCheck,
  Zap,
  MapPin,
  Users,
  TrendingUp,
  Award,
  ArrowRight,
  Compass,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// ------------------------------------
// HELPERS UI
// ------------------------------------
const valueCardStyles = {
  red: {
    iconWrap: 'bg-red-50 text-red-600 border-red-100',
    border: 'border-red-100/70',
    glow: 'from-red-400/10 to-transparent',
  },
  green: {
    iconWrap: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    border: 'border-emerald-100/70',
    glow: 'from-emerald-400/10 to-transparent',
  },
  yellow: {
    iconWrap: 'bg-amber-50 text-amber-600 border-amber-100',
    border: 'border-amber-100/70',
    glow: 'from-amber-400/10 to-transparent',
  },
} as const;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
};

const FloatingGlow = ({ className = '' }: { className?: string }) => (
  <div className={`absolute rounded-full blur-3xl pointer-events-none ${className}`} />
);

// ------------------------------------
// COMPONENTS
// ------------------------------------
const StatCard = ({
  value,
  label,
  sub,
  delay,
}: {
  value: string;
  label: string;
  sub: string;
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    whileHover={{ y: -4, scale: 1.02 }}
    className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 p-6 text-center backdrop-blur-2xl shadow-[0_20px_55px_rgba(255,255,255,0.05)]"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-transparent" />
    <div className="relative z-10">
      <div className="mb-2 text-4xl md:text-5xl font-black text-white">{value}</div>
      <div className="mb-1 text-sm font-bold uppercase tracking-[0.2em] text-blue-200">{label}</div>
      <div className="text-xs text-blue-100/75">{sub}</div>
    </div>
  </motion.div>
);

const ValueCard = ({
  icon: Icon,
  title,
  description,
  color,
}: {
  icon: any;
  title: string;
  description: string;
  color: keyof typeof valueCardStyles;
}) => {
  const styles = valueCardStyles[color];

  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.55 }}
      whileHover={{ y: -8 }}
      className={`relative h-full overflow-hidden rounded-[2rem] border ${styles.border} bg-white/70 p-8 backdrop-blur-2xl shadow-[0_24px_70px_rgba(15,23,42,0.08)]`}
    >
      <div className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-b ${styles.glow}`} />
      <div className="absolute right-0 top-0 p-8 opacity-10">
        <Icon size={100} />
      </div>

      <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border ${styles.iconWrap} shadow-sm`}>
        <Icon size={28} />
      </div>

      <h3 className="mb-3 text-xl font-black text-slate-900">{title}</h3>
      <p className="text-sm leading-relaxed text-slate-600">{description}</p>
    </motion.div>
  );
};

const StorySection = ({
  badge,
  title,
  intro,
  points,
  cardTitle,
  cardText,
  accent,
  icon: Icon,
  reverse = false,
}: {
  badge: string;
  title: string;
  intro: string;
  points: string[];
  cardTitle: string;
  cardText: string;
  accent: 'blue' | 'dark';
  icon: any;
  reverse?: boolean;
}) => {
  const leftCol = reverse ? 'lg:order-2' : 'lg:order-1';
  const rightCol = reverse ? 'lg:order-1' : 'lg:order-2';

  const accentClasses =
    accent === 'blue'
      ? {
          shell: 'bg-white/70 border border-white/60',
          card: 'bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 text-white border border-white/15',
          badge: 'bg-blue-500/10 text-blue-700 border-blue-200',
          iconWrap: 'bg-blue-50 text-blue-600 border-blue-100',
          glow1: 'bg-blue-200/60',
          glow2: 'bg-cyan-200/60',
        }
      : {
          shell: 'bg-slate-950 border border-white/10 text-white',
          card: 'bg-white/10 text-white border border-white/10',
          badge: 'bg-white/10 text-white border-white/10',
          iconWrap: 'bg-white/10 text-yellow-300 border-white/10',
          glow1: 'bg-blue-500/20',
          glow2: 'bg-fuchsia-500/15',
        };

  return (
    <section className="relative min-h-[90vh] py-10 md:py-16">
      <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-14">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className={`${leftCol} lg:sticky lg:top-28`}
        >
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] backdrop-blur-xl ${accentClasses.badge}`}
          >
            <Icon size={14} />
            {badge}
          </span>

          <h3 className="mt-5 text-3xl md:text-5xl font-black leading-tight text-slate-900">
            {title}
          </h3>

          <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
            {intro}
          </p>

          <div className="mt-8 space-y-3">
            {points.map((point, index) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, x: reverse ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/70 px-4 py-4 backdrop-blur-xl shadow-sm"
              >
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                  <Icon size={15} />
                </div>
                <p className="text-sm md:text-base font-medium text-slate-700">{point}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.65, delay: 0.05 }}
          className={`${rightCol} lg:pt-10`}
        >
          <div className={`relative overflow-hidden rounded-[2.5rem] p-6 md:p-8 backdrop-blur-2xl shadow-[0_30px_90px_rgba(15,23,42,0.12)] ${accentClasses.shell}`}>
            <FloatingGlow className={`-top-12 -right-10 h-44 w-44 ${accentClasses.glow1}`} />
            <FloatingGlow className={`-bottom-14 -left-10 h-52 w-52 ${accentClasses.glow2}`} />

            <div className="relative z-10">
              <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border ${accentClasses.iconWrap}`}>
                <Icon size={28} />
              </div>

              <div className={`rounded-[2rem] p-6 md:p-8 shadow-[0_18px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl ${accentClasses.card}`}>
                <h4 className="text-2xl md:text-3xl font-black">{cardTitle}</h4>
                <p className={`mt-4 text-sm md:text-base leading-relaxed ${accent === 'dark' ? 'text-slate-200' : 'text-white/90'}`}>
                  {cardText}
                </p>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  <div className={`rounded-2xl p-4 ${accent === 'dark' ? 'bg-white/8 border border-white/10' : 'bg-white/15 border border-white/20'}`}>
                    <div className="text-xs uppercase tracking-[0.18em] font-black opacity-80">Découverte</div>
                    <div className="mt-2 text-sm font-medium opacity-95">
                      Mieux voir l’offre locale avec plus de contexte et plus de clarté.
                    </div>
                  </div>

                  <div className={`rounded-2xl p-4 ${accent === 'dark' ? 'bg-white/8 border border-white/10' : 'bg-white/15 border border-white/20'}`}>
                    <div className="text-xs uppercase tracking-[0.18em] font-black opacity-80">Activation</div>
                    <div className="mt-2 text-sm font-medium opacity-95">
                      Transformer l’inspiration en action, en réservation ou en achat.
                    </div>
                  </div>

                  <div className={`rounded-2xl p-4 ${accent === 'dark' ? 'bg-white/8 border border-white/10' : 'bg-white/15 border border-white/20'}`}>
                    <div className="text-xs uppercase tracking-[0.18em] font-black opacity-80">Confiance</div>
                    <div className="mt-2 text-sm font-medium opacity-95">
                      Donner une expérience plus rassurante pour le voyageur et le partenaire.
                    </div>
                  </div>

                  <div className={`rounded-2xl p-4 ${accent === 'dark' ? 'bg-white/8 border border-white/10' : 'bg-white/15 border border-white/20'}`}>
                    <div className="text-xs uppercase tracking-[0.18em] font-black opacity-80">Valeur locale</div>
                    <div className="mt-2 text-sm font-medium opacity-95">
                      Mettre le Québec et ses entreprises au centre du parcours.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 font-sans text-slate-900">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 py-24 md:py-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1519885277449-12aa52a1ba6a?q=80&w=2070"
            className="h-full w-full scale-105 object-cover opacity-20"
            alt="Forêt Québec"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_18%,rgba(59,130,246,0.24),transparent_20%),radial-gradient(circle_at_85%_15%,rgba(34,211,238,0.14),transparent_18%),radial-gradient(circle_at_50%_85%,rgba(99,102,241,0.16),transparent_22%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-50" />

          <FloatingGlow className="left-8 top-16 h-44 w-44 bg-blue-500/15" />
          <FloatingGlow className="right-8 top-8 h-56 w-56 bg-cyan-400/10" />
          <FloatingGlow className="bottom-12 left-1/2 h-72 w-72 -translate-x-1/2 bg-indigo-500/10" />
        </div>

        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-blue-200 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.04)]">
              <Target size={12} />
              L’écosystème Forfeo
            </span>

            <h1 className="mb-6 text-4xl md:text-7xl font-black leading-[0.96] tracking-tight text-white">
              Réinventer le voyage
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                au cœur du Québec.
              </span>
            </h1>

            <p className="mx-auto max-w-4xl text-lg md:text-2xl font-medium leading-relaxed text-slate-300">
              La plateforme technologique qui connecte directement les voyageurs aux artisans du tourisme
              québécois, avec une expérience plus intelligente, plus locale et beaucoup plus inspirante.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {[
                'Carte interactive',
                'Découverte régionale',
                'Offres dynamiques',
                'Planification intelligente',
                'Marketplace cadeaux',
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.18 + i * 0.06 }}
                  className="rounded-full border border-white/10 bg-white/8 px-4 py-2 text-xs md:text-sm font-bold text-white/90 backdrop-blur-xl"
                >
                  {item}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative z-20 -mt-20 px-4">
        <div className="container mx-auto">
          <div className="relative overflow-hidden rounded-[3rem] border border-white/10 bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 p-8 md:p-12 shadow-[0_35px_100px_rgba(37,99,235,0.35)]">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
            <FloatingGlow className="-left-10 -top-20 h-60 w-60 bg-white/10" />
            <FloatingGlow className="-bottom-20 -right-10 h-60 w-60 bg-cyan-300/20" />

            <div className="relative z-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <StatCard value="32M+" label="Visiteurs annuels" sub="Au Québec (donnée ISQ)" delay={0} />
              <StatCard value="10 G$" label="Recettes touristiques" sub="Injectés dans l’économie" delay={0.15} />
              <StatCard value="29k+" label="Entreprises" sub="Dans l’industrie touristique" delay={0.3} />
              <StatCard value="22" label="Régions uniques" sub="Accessibles via Forfeo" delay={0.45} />
            </div>
          </div>
        </div>
      </section>

      {/* DEUX PILIERS */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="pointer-events-none absolute inset-0">
          <FloatingGlow className="left-0 top-24 h-80 w-80 bg-blue-100/60" />
          <FloatingGlow className="bottom-10 right-0 h-96 w-96 bg-cyan-100/60" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <motion.div {...fadeUp} transition={{ duration: 0.55 }} className="mb-16 text-center">
            <h2 className="mb-4 text-3xl md:text-5xl font-black text-slate-900">Deux piliers, une vision.</h2>
            <p className="mx-auto max-w-3xl text-lg text-slate-500">
              Forfeo s’articule autour de deux univers complémentaires : un moteur d’exploration touristique
              nouvelle génération et une boutique distincte dédiée à l’achat de cartes-cadeaux physiques de partenaires affiliés.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2 md:gap-12">
            {/* FORFEO EXPLORE */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -6 }}
              className="relative overflow-hidden rounded-[2.5rem] border border-white/60 bg-white/70 p-8 shadow-[0_25px_80px_rgba(15,23,42,0.10)] backdrop-blur-2xl md:p-10"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(59,130,246,0.10),transparent_24%),radial-gradient(circle_at_90%_20%,rgba(34,211,238,0.08),transparent_18%)]" />
              <div className="relative z-10">
                <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full border border-blue-100 bg-blue-50 shadow-sm">
                  <Compass className="text-blue-600" size={42} />
                </div>

                <h3 className="mb-2 text-3xl font-black text-slate-900">
                  Forfeo <span className="text-blue-600">Explore</span>
                </h3>

                <p className="mb-8 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                  Plateforme de découverte, d’inspiration et de planification intelligente
                </p>

                <div className="space-y-4 text-left text-slate-600 font-medium mb-8">
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4">
                    <MapPin className="text-blue-500 w-5 h-5" />
                    Découverte géolocalisée par région
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4">
                    <Zap className="text-blue-500 w-5 h-5" />
                    Offres, signaux et données dynamiques pour mieux agir
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4">
                    <Globe className="text-blue-500 w-5 h-5" />
                    Découverte enrichie, inspiration locale et expérience plus fluide
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-4">
                    <Users className="text-blue-500 w-5 h-5" />
                    Mise en valeur des partenaires et du tissu touristique québécois
                  </div>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 p-4">
                  <p className="text-sm font-bold text-blue-900">
                    “Je découvre, je compare, je planifie et j’explore le Québec autrement.”
                  </p>
                </div>
              </div>
            </motion.div>

            {/* BOUTIQUE FORFEO */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.05 }}
              whileHover={{ y: -6 }}
              className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-slate-950 p-8 text-white shadow-[0_30px_100px_rgba(2,6,23,0.45)] md:p-10"
            >
              <FloatingGlow className="right-0 top-0 h-80 w-80 bg-blue-600/20" />
              <FloatingGlow className="bottom-0 left-0 h-72 w-72 bg-fuchsia-500/10" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.16),transparent_20%),radial-gradient(circle_at_10%_80%,rgba(168,85,247,0.10),transparent_22%)]" />

              <div className="relative z-10">
                <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur-sm">
                  <ShoppingBag className="text-yellow-400" size={42} />
                </div>

                <h3 className="mb-2 text-3xl font-black">
                  Boutique <span className="text-slate-400">Forfeo</span>
                </h3>

                <p className="mb-8 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                  Marketplace de cartes-cadeaux physiques partenaires
                </p>

                <div className="space-y-4 text-left text-slate-300 font-medium mb-8">
                  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
                    <Heart className="text-yellow-400 w-5 h-5" />
                    Achat de cartes-cadeaux physiques de partenaires affiliés
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
                    <Award className="text-yellow-400 w-5 h-5" />
                    Vitrine cadeau claire, premium et simple à comprendre
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
                    <ShieldCheck className="text-yellow-400 w-5 h-5" />
                    Soutien direct à la visibilité et à la valeur des entreprises locales
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
                    <TrendingUp className="text-yellow-400 w-5 h-5" />
                    Complément naturel à Forfeo Explore pour transformer l’intention en achat-cadeau
                  </div>
                </div>

                <div className="mb-6 rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-bold text-yellow-100">
                    “J’achète une carte-cadeau locale que je peux offrir pour vrai.”
                  </p>
                </div>

                <a
                  href="https://forfeo.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 font-black text-white shadow-lg shadow-blue-900/40 transition hover:bg-blue-500 sm:w-auto"
                >
                  Accéder à la boutique <ExternalLink size={16} />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SCROLL STORYTELLING */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-50 py-8 md:py-16">
        <div className="container mx-auto px-4">
          <motion.div {...fadeUp} transition={{ duration: 0.55 }} className="mx-auto mb-14 max-w-4xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/80 px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-blue-700">
              <Target size={14} />
              Scroll storytelling
            </span>
            <h2 className="mt-5 text-3xl md:text-5xl font-black text-slate-900">
              Une page qui raconte vraiment ce que Forfeo fait.
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600">
              Ici, l’idée n’est pas seulement d’afficher du texte. L’idée est de faire comprendre progressivement
              la valeur de la plateforme, comme une marque premium qui se révèle au fur et à mesure du scroll.
            </p>
          </motion.div>

          <StorySection
            badge="Forfeo Explore"
            title="Découvrir le Québec avec plus d’intelligence visuelle."
            intro="Forfeo Explore n’est pas seulement un site d’information. C’est une interface qui aide à voir plus vite, mieux filtrer, mieux comprendre et mieux activer l’offre touristique locale."
            points={[
              'Découverte par région, contexte et logique territoriale.',
              'Mise en avant plus claire des expériences locales et des opportunités.',
              'Expérience plus moderne entre inspiration, repérage et intention.',
              'Parcours plus simple pour passer de “je regarde” à “j’explore”.',
            ]}
            cardTitle="Une couche de découverte plus premium"
            cardText="Forfeo Explore agit comme une porte d’entrée moderne vers le Québec touristique. La plateforme structure l’offre, donne plus de contexte aux voyageurs et rend la découverte plus engageante, plus lisible et plus mémorable."
            accent="blue"
            icon={Compass}
          />

          <StorySection
            badge="Connecteur technologique"
            title="Créer un pont crédible entre voyageurs, partenaires et territoire."
            intro="Forfeo agit comme une couche technologique intermédiaire. Son rôle n’est pas de voler la place aux partenaires, mais de mieux les relier au voyageur grâce à une expérience plus fluide et mieux orchestrée."
            points={[
              'Agrégation plus claire de l’offre sans dénaturer les entreprises locales.',
              'Lecture plus simple des opportunités, du contenu et des parcours.',
              'Expérience plus crédible grâce à une structure cohérente.',
              'Mise en valeur du local dans une logique premium et moderne.',
            ]}
            cardTitle="Un tiers de confiance numérique"
            cardText="Forfeo structure la rencontre entre l’intention, la visibilité, la pertinence et l’activation. La plateforme aide le voyageur à mieux choisir tout en donnant aux partenaires un cadre plus valorisant, plus lisible et plus performant."
            accent="dark"
            icon={ShieldCheck}
            reverse
          />

          <StorySection
            badge="Boutique Forfeo"
            title="Transformer l’inspiration en cadeau concret."
            intro="La boutique Forfeo sur forfeo.com devient un espace distinct, centré sur l’achat de cartes-cadeaux physiques de partenaires affiliés. Elle prolonge naturellement la découverte vers l’achat cadeau."
            points={[
              'Marketplace dédiée aux cartes-cadeaux physiques locales.',
              'Positionnement clair et simple à comprendre pour l’utilisateur.',
              'Nouvelle vitrine commerciale pour les partenaires affiliés.',
              'Complément naturel à l’univers Forfeo Explore.',
            ]}
            cardTitle="Une destination cadeau locale"
            cardText="Là où Forfeo Explore fait découvrir, Forfeo.com permet d’offrir. Cette séparation renforce la clarté de la marque, améliore l’expérience et donne à la boutique une mission forte : faire circuler des cadeaux réels, tangibles et locaux."
            accent="blue"
            icon={ShoppingBag}
          />
        </div>
      </section>

      {/* MISSION / TRUST */}
      <section className="relative overflow-hidden border-t border-slate-100 bg-white py-24">
        <div className="absolute inset-0 pointer-events-none">
          <FloatingGlow className="left-0 top-24 h-96 w-96 bg-blue-100/50" />
          <FloatingGlow className="bottom-0 right-0 h-96 w-96 bg-cyan-100/50" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <motion.div {...fadeUp} transition={{ duration: 0.55 }} className="mx-auto mb-16 max-w-5xl text-center">
            <span className="mb-2 block text-xs font-black uppercase tracking-[0.22em] text-blue-600">
              Notre mission
            </span>
            <h2 className="mb-6 text-3xl font-black text-slate-900 md:text-5xl">
              Connecteur technologique & tiers de confiance
            </h2>
            <p className="text-lg leading-relaxed text-slate-600">
              Forfeo n’est pas une agence de voyage traditionnelle. C’est une infrastructure numérique qui
              connecte la découverte locale, l’inspiration, la mise en valeur des partenaires et les parcours
              d’achat dans un même écosystème cohérent.
              <br />
              <br />
              Notre rôle est de simplifier l’expérience du voyageur tout en augmentant la visibilité et la
              valeur des entreprises locales. Forfeo structure la rencontre entre l’intention, la pertinence,
              la confiance et l’action.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            <ValueCard
              icon={Globe}
              title="Agrégateur neutre"
              description="Nous structurons l’offre touristique sans l’écraser sous une logique uniforme. Chaque entreprise garde son identité pendant que Forfeo améliore la découvrabilité et la mise en relation."
              color="red"
            />
            <ValueCard
              icon={ShieldCheck}
              title="Intermédiaire sécurisé"
              description="Nous renforçons la confiance entre utilisateurs et partenaires grâce à une architecture plus claire, plus fiable et mieux pensée pour soutenir la crédibilité commerciale du réseau."
              color="green"
            />
            <ValueCard
              icon={Zap}
              title="Moteur technologique"
              description="Découverte, contenus, données, logique de parcours et activation travaillent ensemble pour créer une expérience touristique locale plus moderne et plus fluide."
              color="yellow"
            />
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.55 }}
              className="rounded-[2rem] bg-slate-950 p-7 text-white shadow-[0_24px_70px_rgba(2,6,23,0.28)]"
            >
              <div className="mb-4 flex items-center gap-3">
                <Award className="text-cyan-300" size={22} />
                <h3 className="text-xl font-black">Ce que Forfeo apporte au voyageur</h3>
              </div>

              <ul className="space-y-3 text-sm leading-relaxed text-slate-300">
                <li>• Une découverte plus rapide, plus inspirante et mieux structurée du Québec.</li>
                <li>• Une expérience centralisée entre idées, régions, vitrines et opportunités locales.</li>
                <li>• Une meilleure lisibilité des choix et du territoire.</li>
                <li>• Un accompagnement numérique plus premium et plus utile.</li>
              </ul>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="rounded-[2rem] border border-slate-200 bg-white/70 p-7 backdrop-blur-2xl shadow-[0_20px_70px_rgba(15,23,42,0.08)]"
            >
              <div className="mb-4 flex items-center gap-3">
                <Users className="text-blue-600" size={22} />
                <h3 className="text-xl font-black text-slate-900">Ce que Forfeo apporte au partenaire</h3>
              </div>

              <ul className="space-y-3 text-sm leading-relaxed text-slate-600">
                <li>• Une visibilité additionnelle dans un écosystème touristique local premium.</li>
                <li>• Une meilleure exposition des offres, des expériences et des cartes-cadeaux.</li>
                <li>• Un canal moderne pour attirer et valoriser une clientèle qualifiée.</li>
                <li>• Une présence intégrée à un univers de marque plus cohérent et plus fort.</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-[3rem] border border-white/5 bg-gradient-to-br from-indigo-950 via-blue-950 to-slate-900 p-10 text-center shadow-[0_35px_110px_rgba(15,23,42,0.35)] md:p-20">
            <FloatingGlow className="left-0 top-0 h-72 w-72 bg-blue-500/20 mix-blend-screen animate-pulse" />
            <FloatingGlow className="bottom-0 right-0 h-72 w-72 bg-cyan-400/20 mix-blend-screen animate-pulse delay-1000" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(96,165,250,0.15),transparent_25%),radial-gradient(circle_at_80%_80%,rgba(34,211,238,0.12),transparent_20%)]" />

            <div className="relative z-10 mx-auto max-w-3xl">
              <Award className="mx-auto mb-6 h-16 w-16 text-yellow-400" />
              <h2 className="mb-6 text-3xl font-black text-white md:text-5xl">Prêt à explorer autrement ?</h2>
              <p className="mb-10 text-lg text-blue-100">
                Entrez dans un univers où le Québec se découvre avec plus d’intelligence, plus de fluidité
                et plus de valeur pour les voyageurs comme pour les partenaires locaux.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="https://forfeoexplore.com/carte"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-black uppercase tracking-[0.18em] text-slate-900 shadow-xl transition hover:bg-blue-50"
                >
                  Commencer l’exploration
                </a>

                <a
                  href="https://forfeoexplore.com/login"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-white/20 bg-transparent px-8 py-4 font-black uppercase tracking-[0.18em] text-white transition hover:bg-white/10"
                >
                  Devenir partenaire <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
